/**
 * R7 WS-1 §3 E10/E11/E12 — the data-plane reads screens 10 and 11 join against.
 *
 * ⚠️ **WS-1 CREATES this file; WS-2 EXTENDS it** (WS-1 §6). Two shapes the producer carries and
 * WS-1 does not declare, recorded here so WS-2 does not re-derive them: `DataPlacementSummary` also
 * has **`streamId: string | null`** and **`streamName: string | null`** (`dp-admin.service.ts:91-94`)
 * — `streamName` being the resolved `release.release_channels.name` for `platformChannelId`. Adding
 * them is additive.
 *
 * | # | endpoint | producer `file:line` | permission · **kind** | gateway |
 * |---|---|---|---|---|
 * | E10 | `GET /v1/admin/data-planes` | `site-admin-api/.../dp-admin.controller.ts:24-25` | `cp:dp:read` · **site** | `/v1/admin` → site-admin-api (`httproute.yaml:213`) |
 * | E11 | `GET /v1/admin/data-planes/:id/live-status` | same `:38-39` | `cp:dp:read` · **site** | same |
 * | E12 | `GET /v1/admin/data-planes/:id/health` | same `:74-75` | `cp:dp:read` · **site** | same |
 *
 * ⛔ **`version-summary` is NOT here, and it is not usable per row.** The deck credits
 * `GET /v1/admin/data-planes/version-summary` for screen 10's two version columns
 * (`10-tenants.html:519-520`), but that endpoint returns a fleet **distribution** —
 * `DpVersionSummaryEntry { version, count }` (`dp-admin.service.ts:63-66`) — with **no DP identity
 * in it**. Joining it per row is impossible. The two version columns come from E10's own
 * `currentVersion` / `desiredVersion`. Correcting that citation is part of WS-1's job; the endpoint
 * belongs to screen 12 (WS-2).
 */
import type {
  DpAdminLimits,
  PerZoneDpComputePatch,
  DpComputeView,
  MachineTypeCatalogEntry,
  PoolAdvisory,
  PoolComputeView,
  ScalingEventPage,
  ScalingEventQuery,
  StorageClassCatalogEntry,
} from '@zion/sdk-contracts';
/*
 * ⭐ The CP↔DP wire contract, imported rather than re-declared — see {@link DpLiveStatus.services}
 * for the defect that makes this an import instead of a hand-written shape. Precedent in this
 * package: `branding.api.ts` (`OrgBranding`).
 */
import type { ServiceStatus } from '@zion/sdk-cp-dp-contracts';
// 2026-09-04-app-pools WS-3, console (R-013). The route path, the response schema and its inferred
// type. The schema is a value import because `getDpAppPools` parses with it.
import { APP_POOL_ADMIN_PATH, appPoolAdminResponseSchema } from '@zion/sdk-contracts';
import type { AppPoolAdminResponse } from '@zion/sdk-contracts';
import { api } from '../platform/http/client';
import { collectFanOut, type FanOut } from '../panes/tenants-model';
import { scopeStatusParam, type DpScope } from '../panes/dp-state-filter';

/**
 * E10's row. `DataPlacementSummary`, `dp-admin.service.ts:92-176`.
 * ⚠️ Re-measured 2026-08-08 — it read `:80-104`, which stopped being true when WS-2's and WS-4 G3's
 * fields landed. A stale span here is worse than none: it is what made the cert fields look like
 * live-status fields.
 *
 * ⭐ **`desiredVersion` and `currentVersion` stay TWO COLUMNS, always** — "Platform desired" and
 * "Platform running". ⛔ Never merged into a "converged" pill: the entire point is a DP that is
 * version-converged **and** serving errors. `null` renders as the cell text *"none"*, which is the
 * real "never delivered" state rather than an absence.
 */
export interface DataPlaneSummary {
  readonly id: string;
  readonly name: string;
  readonly cloudProvider: string;
  readonly region: string;
  readonly instanceType: string;
  readonly deploymentModel: string;
  readonly status: string;
  /** ⭐ "Platform running". */
  readonly currentVersion: string | null;
  /** ⭐ "Platform desired". */
  readonly desiredVersion: string | null;
  /**
   * ⛔ FREE TEXT WITH NO ENUM — do not branch a rendered verdict on it.
   *
   * The column is `text('version_status').default('current')`
   * (`platform-api/src/db/schema/data-placements.ts:47`) and the values written across the CP
   * include `current`, `upgrading`, `unknown`, `running`, `in_sync` and `up_to_date`. A screen that
   * derived "converged" from it would be naming a claim no producer guarantees.
   */
  readonly versionStatus: string;
  readonly platformChannelId: string | null;
  /**
   * Alias of {@link platformChannelId} (`dp-admin.service.ts:793`). Declared because it is on the
   * wire; ⛔ prefer `platformChannelId`, and do not treat the two as independent facts.
   */
  readonly streamId: string | null;
  /**
   * ⭐ **The RESOLVED release-channel name — and the only safe source of it.**
   *
   * The producer joins `platformChannelId` against **`release.release_channels`**
   * (`resolveChannelNames`, `dp-admin.service.ts:646-660`), which is the table the column actually
   * references. ⛔ `GET /v1/channels` serves a DIFFERENT table (`release.channels`) whose names
   * overlap — the producer's docblock calls it *"the two betas dual-table trap"* — so resolving the
   * id client-side against the channel list can name the wrong channel.
   *
   * `null` ⇒ either no channel is pinned (`platformChannelId === null`) or the id resolved to no
   * record (a dangling reference). `adoption-model.ts`'s `channelCell` keeps those apart.
   */
  readonly streamName: string | null;
  readonly baseDomain: string | null;
  readonly setupPending: boolean;
  readonly orgCount: number;
  readonly plan: string | null;

  /*
   * ── WS-4 G3 / R-14a — the operator-certificate runway ──────────────────────
   *
   * ⛔⛔ **THESE ARE ON `DataPlacementSummary`, THE FLEET-LIST ROW — NOT on `DpLiveStatus`.** Placed
   * here in the producer's own field order (`dp-admin.service.ts`, after `plan`, before
   * `outOfService`), because this interface's contract with that one is positional-by-convention and a
   * field that lands on only one of the two pair is what {@link DataPlaneSummary}'s producer docblock
   * warns about in as many words.
   *
   * ⚠️ **Why this needed checking rather than assuming:** the producer's cert fields sit at
   * `dp-admin.service.ts:132/141/153`, and this file's own note below said `DpLiveStatusSummary` was
   * `:127-156` — so by line number they looked like live-status fields. They are not:
   * `DataPlacementSummary` spans **92-176** and `DpLiveStatusSummary` now starts at **198**. The stale
   * line reference is corrected below. ⛔ Never place a mirrored field by line number.
   *
   * Context, so a tidy-up does not delete them: the alerts covering this condition were deleted when
   * the metric broke (#830), **after a two-day production outage on 2026-06-02 caused by an expired
   * operator certificate**. This is currently the only working surface for it.
   */

  /**
   * ISO-8601 expiry of the DP's newest **unrevoked** operator certificate, or `null`.
   *
   * ⛔ `null` means **never enrolled** — no unrevoked cert on record — and it is NOT "fine". A screen
   * renders it as `notcollected`, never as `empty` and never as `0 days`.
   *
   * ⛔ **`cert_pem` and `fingerprint` are never on the wire and must never be asked for**: the
   * fingerprint is an authz *input*, so echoing it to a browser hands out an authentication factor.
   */
  readonly operatorCertExpiresAt: string | null;
  /**
   * ISO-8601 issuance of **THAT SAME cert row** — never of another.
   *
   * ⚠️ Declared because the pair is what makes the verdict auditable on screen; the pairing is
   * load-bearing server-side (the threshold is `(expiresAt − issuedAt) / 3`, which is why the query is
   * `DISTINCT ON` rather than `GROUP BY MAX(expires_at)`). ⛔ A client must NOT recompute the verdict
   * from these two — that is a second copy of the server's arithmetic and drifts the day either moves.
   * Render the **absolute** expiry as a fact and take the verdict from the boolean below.
   */
  readonly operatorCertIssuedAt: string | null;
  /**
   * ⭐ **SERVER-OWNED verdict**: has the DP's cert-rotator entered its renewal window without
   * succeeding?
   *
   * ⛔ **`boolean | null`, and the `null` is the whole point.** A never-enrolled data plane reporting
   * `false` renders as *"renewal fine"* for a data plane with no certificate at all — absence rendered
   * as health, the #1202 shape this console has already shipped once. NULL when there is nothing to
   * judge; never `false`.
   *
   * ⚠️ `true` maps to **`caution`, not `bad`**: the cert has not expired, it is inside the window that
   * exists so a transient `/cert/sign` failure has many cycles to retry. And the `null` arm maps to
   * **`none`, never `good`** — `VERDICT_RANK` ranks `none` above `good` precisely so an unmeasured
   * input can only lower a row's confidence.
   */
  readonly operatorCertRenewalOverdue: boolean | null;

  /** `Date` server-side ⇒ ISO strings on the wire. */
  readonly createdAt: string;
  readonly updatedAt: string;

  /**
   * The kept cloud project or account of this data plane, from the record
   * `provisioning.retained_projects` (plan 2026-10-22-retained-tenant-projects, D-17). The server
   * omits the key when no record exists for the row. A destroy writes the record, and so does the
   * provisioning sweep for a failed onboarding. A row whose placement is gone still comes back,
   * with status `destroyed` or `failed`, and carries this field. The Data planes page renders it
   * (D-18) through `retainedProjectView` in `panes/retained-project.ts`.
   */
  readonly retainedProject?: RetainedProject;

  /**
   * The kept cloud project or account of each removed cross-cloud satellite of this data plane, from
   * the records of `provisioning.retained_projects` whose `satellite_region` is set (plan
   * 2026-10-24-aws-dp-identity-and-lifecycle, D-21, R-010, R-035). The server sends the records newest
   * first, and omits the key when no satellite record names the data plane. A placement row and a row
   * that the server made from a home record (`placementMissing`) can both carry it. When the data plane
   * has no placement row and no home record, its satellite records make their own row, with no
   * `retainedProject` and the `instanceType` `unknown` (rule 5 of `DpAdminService.list()`, review row
   * WS-13-P2-R2).
   *
   * The Data planes page renders each record under the `retainedProject` note through
   * `retainedSatelliteProjectView` in `panes/retained-project.ts`. `hasPlacement` reads it with
   * `instanceType` to tell a row that satellite records alone made, which has no placement (review row
   * WS-14-P3-R7).
   */
  readonly retainedSatelliteProjects?: readonly RetainedSatelliteProject[];
}

/**
 * The facts that a home record and a satellite record share: every member of {@link RetainedProject}
 * other than `placementMissing`, with the same meaning. `retainedProjectView` reads this shape, so it
 * serves both records.
 */
export type RetainedRecordFacts = Omit<RetainedProject, 'placementMissing'>;

/**
 * The kept project or account of one removed cross-cloud satellite: a copy of
 * `DpRetainedSatelliteProject` in `dp-retained-project.ts` of site-admin-api, the producer of the
 * `retainedSatelliteProjects` member of a list row. Both copies change together.
 *
 * - `region`: the satellite region, the record's `satellite_region`. The home record names the home
 *   region; this one names the region of the removed satellite.
 * - the ten members of {@link RetainedRecordFacts}, with the meaning of {@link RetainedProject}:
 *   - `cloud` is the satellite's cloud, which can differ from the home's;
 *   - `nameBlocked` is `true` while the TenantCloudBase `tcb-<dpName>-sat-<region>` still exists.
 *
 * No `placementMissing` member: a satellite record rides the row of its data plane, and a row that
 * satellite records alone made has no `retainedProject`. The record holds facts only, and the
 * `cp:dp:read` grant already reads the AWS account id.
 */
export interface RetainedSatelliteProject extends RetainedRecordFacts {
  readonly region: string;
}

/**
 * The kept project or account on a list row: a copy of the `retainedProject` field of the
 * site-admin-api list row (`DataPlacementSummary` in `dp-admin.service.ts`). The Contracts section of
 * `00-index.md` of plan 2026-10-22-retained-tenant-projects fixes the shape, and both copies change
 * together.
 *
 * - `cause`: `destroyed` for a destroy of the data plane; `onboarding_failed` for a project or
 *   account that a failed onboarding left. The second has no destroyed time and no retention end.
 * - `cloud`: the cloud of the project or account.
 *   - `gcp` fills the two `gcp*` fields.
 *   - `aws` fills the two `aws*` fields.
 *   - The other pair is `null`.
 *   An id is also `null` when the TenantCloudBase status held none at record time.
 * - `destroyedAt`, `retentionEndsAt`: ISO 8601, `null` for `onboarding_failed`. The end is the
 *   destroyed time plus `DP_AUDIT_RETENTION_MS` (366 days) on the server.
 * - `retentionEnded`: the server's verdict, its clock past `retentionEndsAt`. The console shows this
 *   verdict and compares no date with its own clock.
 * - `nameBlocked`: the TenantCloudBase `tcb-<name>` still exists, so a new onboarding of the same
 *   data plane name refuses until a person deletes it.
 * - `placementMissing`: the server made the row from the record alone, because no placement row
 *   exists (R-012). The server puts such a row in the `out-of-service` class only. The console reads
 *   the flag through `hasPlacement` in `panes/retained-project.ts`:
 *   - The Data planes page shows the row in every scope, with the name as plain text and with no live
 *     status read.
 *   - The data plane pickers (`NewTenantPane`, `DestinationsTab`, `data-plane-choices-context.tsx`)
 *     and the in-flight list (`inFlightDataPlanes`) drop the row.
 *
 * The record holds facts only. The `cp:dp:read` grant already reads the AWS account id (12 digits).
 * The console shows it and writes it to no log.
 */
export interface RetainedProject {
  readonly cause: 'destroyed' | 'onboarding_failed';
  readonly cloud: 'gcp' | 'aws';
  readonly gcpProjectId: string | null;
  readonly gcpProjectNumber: string | null;
  readonly awsAccountId: string | null;
  readonly awsAccountName: string | null;
  /** ISO 8601. */
  readonly destroyedAt: string | null;
  /** ISO 8601. */
  readonly retentionEndsAt: string | null;
  readonly retentionEnded: boolean;
  readonly nameBlocked: boolean;
  readonly placementMissing: boolean;
}

/**
 * E11's body — a SUBSET of `DpLiveStatusSummary` (`dp-admin.service.ts:198+`); the fields this
 * workstream reads, declared `readonly`.
 *
 * ⭐ **The "cannot assess" verdict is SERVER-COMPUTED and needs no client threshold.**
 * `operatorStale` and `operatorUnresponsive` come straight off the producer (`:140-141`).
 * ⛔ Do **not** recompute staleness in the browser from `reportedAt` and a hardcoded 10 minutes: the
 * deck's copy names "the threshold is 10", and a second threshold in the client would disagree with
 * the server's the day either moves. Render the **age** from `reportedAt` (a fact) and the
 * **verdict** from the booleans (the server's answer).
 */
export interface DpLiveStatus {
  readonly dataPlaneId: string;
  readonly platformVersion: string;
  readonly health: string;
  readonly reportedAt: string;
  readonly receivedAt: string;
  readonly needsRefresh: boolean;
  /** ⭐ The cannot-assess signal. */
  readonly operatorStale: boolean;
  /** ⭐ The stronger one. */
  readonly operatorUnresponsive: boolean;

  /**
   * The operator version the data plane named in its last report, from `DpLiveStatusSummary` in
   * `dp-admin.service.ts`. Ruling R-004 of the 2026-10-07 drill-in index declares it optional: the
   * Overview Version card renders an absence when the field is missing or empty.
   */
  readonly operatorVersion?: string;
  /**
   * The Helm chart version of the `zion-data-plane` release the data plane named in its last
   * report, from the same projection. Optional under R-004, as `operatorVersion` is.
   */
  readonly helmChartVersion?: string;

  /*
   * ── WS-2 §5.2 additions (authorized by the lead, ACK-ws2-5) ───────────────
   * Purely additive; every WS-1 call site keeps compiling. Screen 13's state panel needs these.
   */

  /**
   * Per-service status, `dp-admin.service.ts:138`.
   *
   * ## ⛔ THIS WAS TYPED `{ status?: string }` AND NO SUCH FIELD HAS EVER EXISTED
   *
   * The consumer invented a field. `serviceRows()` read `value.status ?? null`, so the Status column
   * on the DP Overview tab rendered *"— not stated"* for **every service on every data plane**, and
   * always had. Traced end-to-end rather than assumed — ⛔ this file's own doctrine 500 lines below
   * warns that CP re-shapes between the DP wire and the browser (`DpPodName`: *"the DP's own contract
   * says `items`; this route says `pods`"*), so the wire contract alone was not sufficient evidence:
   *
   *  1. **The wire.** `ServiceStatusSchema` (`contracts-cp-dp/src/status-report.schemas.ts:17`) is
   *     `.strict({ version, health, readyPods, desiredPods })`. `.strict()` means a producer that
   *     *did* send `status` would have its **entire status report 400'd** at ingest.
   *  2. **The persist hop.** `operator-api.service.ts:1917` stores `report.services` verbatim.
   *  3. ⭐ **The hop that mattered — CP does NOT re-shape this one.** `dp-admin.service.ts:138`
   *     declares `services: Record<string, ServiceStatus>` off that same published contract and
   *     `:228` passes `row.services` straight through. Unlike the pods route, what reaches the
   *     browser IS the wire.
   *  4. **History.** `git log -S` on the schema: `status` was never a key, in any revision, ever.
   *     The pre-zod interface (`1b9b4b74f`) was `{currentVersion, desiredVersion, podsReady,
   *     podsTotal, health, lastMigrationVersion}` — `health` has been the signal since day one.
   *
   * ⛔ **Nothing caught it because `api.get<DpLiveStatus>()` is an unchecked assertion**, not a
   * parse. A hand-written read type is a *claim* about the wire, and this one was false in the exact
   * direction this file already names as the dangerous one: *"omitting a field you do not read is
   * fine; declaring one the producer never sends, or mistyping its nullability, is what breaks
   * silently."*
   *
   * ⇒ ⭐ **Typed by IMPORTING the contract, so TypeScript is now the gate.** Any future drift between
   * this read type and the CP↔DP contract is a compile error rather than a column of dashes — which
   * is strictly better than any text gate we could write for it.
   *
   * ⚠️ **All four fields are REQUIRED on the wire**, so there is no absence branch here and must not
   * be one: an unreachable arm can only ever be greened by a fixture that lies about the wire.
   */
  readonly services: Readonly<Record<string, ServiceStatus>>;

  /**
   * ⛔ **`null` MEANS *EMPTY*, NOT *NOT-COLLECTED*, AND `[]` NEVER ARRIVES.**
   *
   * The writer collapses an empty list to `null`:
   * `release-engine/src/modules/operator-api/operator-api.service.ts:2901-2904` —
   * `lastEnvelopeErrors.length > 0 ? array : null`. The read side says the same in its own
   * docblock — `DpLiveStatusSummary.lastEnvelopeErrors` in `dp-admin.service.ts` (`:214-226`):
   * *"It is therefore an `empty`, not a `notcollected`"*.
   *
   * ⚠️ ANCHORED ON THE SYMBOL, because the line number here has already drifted once: it read
   * `:147-155`, which is that file's `operatorCertRenewalOverdue` docblock and does not contain the
   * quote. Re-derive from the field name, never from the number.
   *
   * ⇒ mapping `null` to `notcollected` would render the **common healthy case** as "we could not
   * ask" — R7-DEC-7's collapse running in reverse, and the same defect family as #1202. The
   * genuine not-collected is **not a field value at all**: `getLiveStatus` throws
   * `NotFoundException` when no `release.dp_live_status` row exists
   * (`dp-admin.service.ts:975-981`), i.e. **E11 404 = this data plane has never reported**.
   *
   * ⛔ Do not write a `[]` branch. It is unreachable, and an uncoverable branch can only be greened
   * by a suppression or by a fixture that lies about the wire. `dp-model.ts` owns the mapping.
   */
  readonly lastEnvelopeErrors: readonly DpEnvelopeError[] | null;

  /**
   * The producer types this member `Record<string, unknown>` (`DpLiveStatusSummary.apps` in
   * `dp-admin.service.ts`), so it has no structure. Render counts only, never the structure: no
   * contract describes this shape, so anything drawn from its interior is a guess that typechecks.
   *
   * The row carries no `instances` member. R2 of the app-pools runtime deletion (R-081) removed it
   * from the status wire and from the site-admin-api live-status row, so a read type that kept it
   * would declare a field the producer never sends.
   */
  readonly apps: Readonly<Record<string, unknown>>;
}

/**
 * One entry of `release.dp_live_status.last_envelope_errors` (`dp-admin.service.ts:179-195`).
 *
 * ⚠️ `kind` is a **superset** of the CP↔DP wire enum: `deriveEnvelopeHealth` appends CP-SYNTHESIZED
 * entries with `kind: 'fixture-unhealthy'`, a value the operator never sends. Typed as `string`
 * here rather than narrowed, because narrowing to the wire enum would be a lying read type — green
 * forever, wrong at runtime the first time a fixture is degraded.
 *
 * ## ⛔ THIS TYPE HAD NO `errors` MEMBER AT ALL, WHICH MADE THE CAUSE UNREACHABLE (#1734)
 *
 * It carried `message?` and `at?` — two fields no writer of this column emits — and omitted
 * `errors`, the one they all emit. So the Overview tab rendered `{e.message}`, which was
 * `undefined` for EVERY row on EVERY data plane, and there was nothing else it could have read.
 * Same failure this file already records for {@link DpLiveStatus.services}: *"declaring one the
 * producer never sends, or mistyping its nullability, is what breaks silently."* Nothing caught it
 * because `api.get<DpLiveStatus>()` is an unchecked assertion, and because the test fixture was
 * written FROM this type, so it produced text and passed.
 *
 * Traced end to end rather than assumed:
 *
 *  1. **The wire.** `EnvelopeErrorSchema` (`contracts-cp-dp/src/status-report.schemas.ts`) is
 *     `.strict()` with a REQUIRED `errors` array and NO top-level `message`. `.strict()` means a
 *     producer that sent one would have its entire status report 400'd at ingest.
 *  2. **The operator hop.** `operator-api.service.ts`'s `deriveEnvelopeHealth` persists the
 *     validated rows verbatim.
 *  3. **The synthesized hop.** The same function appends
 *     `{ appId, fixtureId, fixtureVersion, kind: 'fixture-unhealthy', errors: [{ instancePath,
 *     message: "fixture health is '<h>' (readyReplicas=N/M)" }] }` — the same shape, and CP failing
 *     to read text CP itself wrote.
 *  4. **The read hop.** `dp-admin.service.ts` types the column
 *     `Omit<EnvelopeError, 'kind'> & { kind: … }` and passes the rows straight through.
 *
 * ⚠️ A top-level `message` DOES exist on the OTHER envelope-error surface — the DP shell's banner
 * over `release.app_degradations`, whose hand-built writer emits it — which is why
 * `describeEnvelopeError` in `@zion/sdk-cp-dp-contracts` still reads it first. That function takes
 * `unknown`, so this type need not declare a field this column's producers never write.
 *
 * ⛔ `errors` is OPTIONAL here and REQUIRED on the wire, and both are correct. This is a `jsonb`
 * column with no database constraint, read through an unchecked assertion, holding rows persisted
 * by whatever validated them at the time. Declaring it required would be a claim the read path
 * cannot make good. It costs no branch: `describeEnvelopeError` presence-guards every field.
 */
export interface DpEnvelopeError {
  readonly kind: string;
  readonly errors?: readonly {
    readonly instancePath: string;
    readonly message: string;
  }[];
}

/**
 * E12's body.
 *
 * ⚠️ **Two-hop provenance, and site-admin-api types it `Promise<unknown>`.**
 * `DpAdminService.getAggregatedHealth()` is an untyped passthrough of provisioning-svc's
 * `GET /v1/data-planes/:id/health`; the real shape is the return annotation on
 * `ProvisioningService.getHealth()`. ⇒ this declaration is the only place that shape is written
 * down between the two services, so a producer change surfaces here as a runtime blank rather than
 * a typecheck failure. Reported, not papered over.
 *
 * ⛔ **`healthy` is FLEET MEMBERSHIP, NOT A HEALTH PROBE**: `!isOutOfServiceDpStatus(dp.status)`
 * (`ProvisioningService.getHealth()`), the one shared predicate in `@zion/sdk-contracts`'s
 * `dp-lifecycle-status.ts`. ⚠️ It reads `true` for `failed`, `degraded`, `draining`, `disabling`,
 * `disabled` and `restoring` — those are recoverable faults, and a faulty data plane is still part
 * of the operating fleet. Only `destroying`, `destroyed` and `decommissioned` read `false`. ⛔ So
 * `healthy === true` does not mean the data plane is well, and it is the same class of claim as
 * E1's `issues[]`. Read `status` for the honest per-row fact. ⛔ It must never be rendered as an
 * application verdict.
 *
 * ✅ `lastError` is already scrubbed server-side — `redactSecrets(lastError)`, in
 * `ProvisioningService.getHealth()`'s return object — so it is safe to render verbatim.
 */
export interface DpAggregatedHealth {
  readonly dataPlaneId: string;
  readonly status: string;
  readonly workflowState: string | null;
  readonly jobStatus: string | null;
  readonly jobId: string | null;
  readonly lastError: string | null;
  /** `Date | null` server-side ⇒ an ISO string, or `null`. */
  readonly lastTransitionAt: string | null;
  /** ⛔ Fleet membership only, `true` for every recoverable fault — see this interface's docblock. */
  readonly healthy: boolean;
}

/** The site-wide permission behind all three. */
export const DP_READ_PERMISSION = 'cp:dp:read';

/**
 * ⛔ **SITE-WIDE, on a DIFFERENT SERVICE from every read in this file.**
 * `POST /v1/data-planes` is `@RequirePermission('cp:dp:create')` on **provisioning-svc**
 * (`provisioning.controller.ts:68-69`), while every read here is site-admin-api's `/v1/admin/…`.
 * ⇒ `useHasPermission(DP_CREATE_PERMISSION)` takes no id — there is no DP yet to scope a grant to.
 */
export const DP_CREATE_PERMISSION = 'cp:dp:create';

export const DATA_PLANES_PATH = '/v1/admin/data-planes';

/**
 * ⛔ **THE CREATE PREFIX IS NOT {@link DATA_PLANES_PATH}.** Reads are proxied by site-admin-api at
 * `/v1/admin/data-planes`; the create is provisioning-svc's own `/v1/data-planes`
 * (`@Controller('v1/data-planes')`).
 *
 * ⚠️ And `GET /v1/data-planes/:id` on that same controller is **410 Gone** by design — the deprecation
 * is on the read, not the collection. So this prefix is correct for `POST` and wrong for everything
 * else this file does, which is exactly why it gets its own named constant rather than a literal at
 * the call site.
 */
export const DP_PROVISIONING_PATH = '/v1/data-planes';

/**
 * The regions a data plane can be created in: provisioning-svc's own literal route under
 * {@link DP_PROVISIONING_PATH}, served above its `:id` route (plan 2026-10-06-dp-region-list, D4).
 * It carries {@link DP_READ_PERMISSION}. With no query it lists the GCP regions; {@link dpRegionsPath}
 * adds `?provider=aws` for the AWS regions and accounts.
 */
export const DP_REGIONS_PATH = '/v1/data-planes/regions';

export function dataPlanePath(dpId: string): string {
  return `${DATA_PLANES_PATH}/${encodeURIComponent(dpId)}`;
}

export function dpStatesPath(): string {
  // ⚠️ A FLEET-wide read with no DP identity in it — a sibling of
  // `version-summary`, not a per-DP route. Served before `:id` on the server
  // for exactly that reason.
  return `${DATA_PLANES_PATH}/states`;
}

/**
 * The fleet filter's option set and the whole-table counts.
 *
 * ⛔ **The options come from the SERVER, never from the loaded rows.** Deriving
 * them client-side is what made the filter incomplete: once the list excludes
 * out-of-service DPs by default, a data-derived option list can never offer
 * `destroyed`, so the operator cannot tick the box that would bring them back.
 * A filter whose options come from the filtered data is structurally incapable
 * of reaching the values it hides.
 *
 * ⛔ **`counts.total` is the server's and cannot be computed here** — the
 * client never receives the withheld rows, so a hidden-count derived from
 * visible rows is always zero. That is the reassuring-but-false answer.
 */
export interface DpStateFacets {
  readonly states: readonly string[];
  readonly outOfService: readonly string[];
  readonly counts: {
    readonly total: number;
    readonly inService: number;
    readonly outOfService: number;
  };
}

export function fetchDpStates(): Promise<DpStateFacets> {
  return api.get<DpStateFacets>(dpStatesPath());
}

export function dpLiveStatusPath(dpId: string): string {
  return `${dataPlanePath(dpId)}/live-status`;
}

export function dpHealthPath(dpId: string): string {
  return `${dataPlanePath(dpId)}/health`;
}

/**
 * The default scope of a fleet read, as a server class name: the in-service data planes.
 *
 * `GET /v1/admin/data-planes` returns the whole table when `status` is absent. Hiding retired data
 * planes is this console's view decision, so every default read says so. The members of the class
 * stay on the server, in `site-admin-api`'s `dp-admin/dp-lifecycle.ts`; a client copy of the list
 * would compile and still disagree with the server one day.
 */
export const FLEET_DEFAULT_STATUS_CLASS = 'in-service';

/**
 * The query string of a fleet read for one scope (D5 of the data planes round 2 plan).
 *
 * - `in-service`: `?status=in-service`. The default, so a caller with no scope gets it.
 * - `out-of-service`: `?status=out-of-service`.
 * - `all`: the empty string, which the server reads as every state.
 *
 * The pane puts this exact string in its `useApiResource` key, because the hook keys by the id it
 * gets and not by the URL. One function for the key and the URL keeps the two from drifting, so a
 * scope change never shows the rows of the previous scope.
 */
export function dataPlanesQuery(scope: DpScope = FLEET_DEFAULT_STATUS_CLASS): string {
  const status = scopeStatusParam(scope);
  return status === undefined ? '' : `?status=${encodeURIComponent(status)}`;
}

/**
 * `GET /v1/admin/data-planes` for one scope. With no scope it sends `?status=in-service`, so the
 * callers that list data planes for a picker never load the destroyed ones. Pass `all` to load every
 * state.
 */
export function listDataPlanes(
  scope: DpScope = FLEET_DEFAULT_STATUS_CLASS,
): Promise<readonly DataPlaneSummary[]> {
  return api.get<readonly DataPlaneSummary[]>(`${DATA_PLANES_PATH}${dataPlanesQuery(scope)}`);
}

// ── E-NEW-2: data-plane CREATE. ⛔ provisioning-svc, `/v1/data-planes`, `cp:dp:create`. ──

/**
 * The create body, in **WIRE** shape — the subset of `createDataPlaneSchema`
 * (`provisioning-svc/.../dto/create-data-plane.dto.ts`) this console sends.
 *
 * ## ⛔ WHAT IS REQUIRED IS NOT WHAT YOU WOULD GUESS
 *
 * Five keys have **no `.optional()`** on the producer, and two of the five are ones a form is likely
 * to treat as advanced settings:
 *
 * | key | producer constraint |
 * |---|---|
 * | `name` | `z.string()` |
 * | `orgIds` | `z.array(z.string().uuidv4()).min(1)` — ⛔ **≥1, so a DP cannot exist before a tenant does** |
 * | `platformChannelId` | `z.string().uuidv4()` — ⛔ *"Required — no implicit channel selection."* |
 * | `cloudProvider` | `z.enum(['local','gcp','aws'])`. `azure` is absent: the server removed it on 2026-08-29, and `aws` came back with the AWS port (plan 2026-08-25-dp-aws-port, R-029, R-056). |
 * | `instanceType` | `z.enum(['shared','dedicated'])` |
 * | `deploymentModel` | `z.enum(['vendor_managed','byoc'])` |
 *
 * …and `region` is conditionally required by a `superRefine`: **omitted it is fine for `local` and a
 * 400 for everything else.** ⇒ it is modelled optional here and supplied by
 * {@link buildCreateDataPlaneWire}, which is where the condition is enforced once.
 *
 * ## `baseDomain` is not on this wire
 *
 * The producer still accepts an optional `baseDomain` as an operator override, and no longer requires
 * it for any provider (plan `2026-08-13-cert-scope-split/06-basedomain-migration.md` §4.2, guard F at
 * release N). provisioning-svc derives the apex from `ZION_DP_BASE_DOMAIN`. This console has no
 * override control, so it never sends the key; see item 4 of {@link buildCreateDataPlaneWire}. The
 * response's `baseDomain` on {@link DataPlaneSummary} is a display value and stays.
 *
 * ## ⛔ EVERY OTHER KEY IS DELIBERATELY ABSENT, AND ABSENT IS A REAL VALUE
 *
 * `enabledMachineTypes`, `infra`, `defaultApp`, `nats`, `coreApi`, `shell`, `redis`, `plan`,
 * `llmCredentials`, `defaultAppChannels` and `claimHeaderProducer` are all optional **with
 * documented server-side defaults** (D15.10 *never zero*; the WS-1 starter sizing; *"omitted = no override"* for
 * the claim-header producer, where `false` is explicitly NOT the same as absent). `maxOrgs` (#904)
 * is on the wire, and only when the operator sets a cap: omitted means unlimited. Omitting them is
 * accepting a documented default, which is a different act from failing to offer a control — the pane
 * says so on screen rather than letting the absence read as a missing feature.
 *
 * ⛔ The schema is `.strict()` (deep-stricted by `ZionStrictValidationPipe`), so an unknown top-level
 * key is a **400**, not an ignored field. Adding one here without adding it there fails at the wire.
 */
export interface CreateDataPlaneWire {
  readonly name: string;
  /** ⛔ ≥1 uuid. See the type docblock. */
  readonly orgIds: readonly string[];
  readonly platformChannelId: string;
  readonly cloudProvider: 'local' | 'gcp' | 'aws';
  readonly instanceType: 'shared';
  readonly deploymentModel: 'vendor_managed';
  /** ⛔ Required unless `cloudProvider === 'local'` — a `superRefine`, not a field-level rule. */
  readonly region?: string;
  /**
   * The tenant cap, a whole number in 1..`MAX_ORGS_MAX` (`maxOrgsSchema`). Absent means unlimited,
   * the server default. The server answers 409 when the chosen `orgIds` exceed it.
   */
  readonly maxOrgs?: number;
  /**
   * The tenant's Google Cloud project ID. `buildCreateDataPlaneWire` sends it only when
   * `cloudProvider === 'gcp'`, as the operator typed it, and only after the form checked it against
   * `GCP_PROJECT_ID_PATTERN` from `@zion/sdk-cp-dp-contracts` (ruling R-012). A local create carries
   * no key. provisioning-svc refuses a gcp create with no project (`GCP_PROJECT_MISSING`), a project
   * outside the grammar (`GCP_PROJECT_INVALID`) and the control plane's own project
   * (`GCP_PROJECT_IS_CP_PROJECT`), each a 400 `{ code, message }` (ruling R-002). The CP has no
   * default project for a data plane.
   */
  readonly gcpProject?: string;
  /**
   * The AWS inputs of an AWS data plane (plan 2026-08-25-dp-aws-port, R-029 item 1).
   * `buildCreateDataPlaneWire` sends it only when `cloudProvider === 'aws'`, and an aws body always
   * carries it. The producer refuses an aws body without it, and the key on any other provider.
   *
   * - `accountId`: one of the 12-digit accounts the regions read lists for `aws`
   *   ({@link DataPlaneRegionChoices.accountIds}). The server refuses an account outside
   *   `AWS_DP_ACCOUNT_IDS` with a 400.
   * - `availabilityZones`: two different zones of the region, zone A first. Absent: the server
   *   takes the default pair of the region. The server refuses a zone of another region.
   *
   * The partition, the VPC range and the subnets are absent by design: the server derives the
   * partition from the region and takes the ranges from its allocator, and the strict schema answers
   * 400 for each of those keys.
   */
  readonly aws?: {
    readonly accountId: string;
    readonly availabilityZones?: readonly [string, string];
  };
}

/**
 * `DataPlaneResponseDto` — the create response.
 *
 * ⚠️ **It carries NO setup token, and that is a deliberate platform property** (#477/#505, D31
 * *store-nothing*): the token is minted during bootstrap and pushed to the DP, and the CP stores no
 * token material. `toDpResponse` never includes one. ⛔ A create screen must therefore not promise a
 * token — it directs the operator to reveal one from the DP's own settings, where revealing rotates.
 *
 * ⚠️ Only `id` and `name` are consumed by this console; the rest of the DTO is re-read from the DP
 * detail screen's own endpoint rather than seeded from here.
 */
export interface CreatedDataPlane {
  readonly id: string;
  readonly name: string;
}

/** E-NEW-2. ⛔ {@link DP_PROVISIONING_PATH}, not {@link DATA_PLANES_PATH}. */
export function createDataPlane(body: CreateDataPlaneWire): Promise<CreatedDataPlane> {
  return api.post<CreatedDataPlane>(DP_PROVISIONING_PATH, body);
}

/**
 * The cloud whose regions `GET /v1/data-planes/regions` lists: `gcp` (the server default) or `aws`
 * (plan 2026-08-25-dp-aws-port, R-029 item 2). The server's `dpRegionsQuerySchema` admits these two.
 */
export type DataPlaneRegionProvider = 'gcp' | 'aws';

/**
 * The request path of the region read for `provider`, and the `useApiResource` key of the create
 * form's region read.
 *
 * - `gcp`: {@link DP_REGIONS_PATH} with no query, the exact request the form sent before the AWS
 *   port, which the server reads as `gcp`.
 * - `aws`: {@link DP_REGIONS_PATH} with `?provider=aws`.
 *
 * Pure. The argument is a member of a fixed union, so no caller text reaches the URL.
 */
export function dpRegionsPath(provider: DataPlaneRegionProvider): string {
  return provider === 'aws' ? `${DP_REGIONS_PATH}?provider=aws` : DP_REGIONS_PATH;
}

/**
 * The wire body of `GET /v1/data-planes/regions`, declared here as `listPlatformChannels` declares
 * its own (plan 2026-10-06-dp-region-list, D5). The server's `DpRegionsResponse` is the producer.
 * `accountIds` is on the `aws` answer alone (plan 2026-08-25-dp-aws-port, R-056 item 1).
 */
export interface DataPlaneRegionsResponse {
  readonly regions: readonly string[];
  readonly accountIds?: readonly string[];
}

/**
 * What the create form can offer for one cloud: the regions, and the AWS accounts.
 *
 * - `regions`: the server's list, in server order.
 * - `accountIds`: the AWS accounts a create may name, in the order of the server setting. Always
 *   `[]` for `gcp`. For `aws`, `[]` means that no aws create can pass, so the form blocks the submit.
 */
export interface DataPlaneRegionChoices {
  readonly regions: readonly string[];
  readonly accountIds: readonly string[];
}

/**
 * The region read of the create form for one cloud. Called by `NewDataPlanePane` through
 * `useApiResource`, keyed by {@link dpRegionsPath}.
 *
 * - `gcp`: `{ regions, accountIds: [] }`. The `gcp` answer has no `accountIds` key, and the
 *   function reads none.
 * - `aws`: `{ regions, accountIds }`. An `aws` answer with no `accountIds` key (a server from before
 *   the AWS port) gives `accountIds: []`, so the form states that no account is listed.
 *
 * No client-side sort: the server's order is the display order. A 403 or any other failure rejects
 * with the `ApiError`. No other side effect.
 */
export async function listDataPlaneRegions(provider: DataPlaneRegionProvider): Promise<DataPlaneRegionChoices> {
  const body = await api.get<DataPlaneRegionsResponse>(dpRegionsPath(provider));
  return {
    regions: body.regions,
    accountIds: provider === 'aws' ? (body.accountIds ?? []) : [],
  };
}

export function getDpLiveStatus(dpId: string): Promise<DpLiveStatus> {
  return api.get<DpLiveStatus>(dpLiveStatusPath(dpId));
}

export function getDpAggregatedHealth(dpId: string): Promise<DpAggregatedHealth> {
  return api.get<DpAggregatedHealth>(dpHealthPath(dpId));
}

/**
 * §4.3 — the per-DP `live-status` fan-out, **bounded and reported**.
 *
 * ⛔ `Promise.allSettled`, never `Promise.all`: one 500 must not blank the cannot-assess band for the
 * whole fleet. ⛔ And never a SILENT partial — the failed ids come back in {@link FanOut.failedIds} so
 * the screen can say *"k of n data planes could not be checked"* (A16). An unreported failure count is
 * the "did not look = found nothing" collapse.
 *
 * ⚠️ **The cost is one request per data plane**, and it is stated rather than hidden: there is no
 * fleet-wide live-status endpoint (the census has only `…/:id/live-status`), so the fan-out is forced
 * by the producer, not chosen.
 */
export async function fetchLiveStatuses(
  dpIds: readonly string[],
): Promise<FanOut<DpLiveStatus>> {
  const settled = await Promise.allSettled(dpIds.map((id) => getDpLiveStatus(id)));
  return collectFanOut(dpIds, settled);
}

/*
 * ═══════════════════════════════════════════════════════════════════════════
 * R7 WS-2 §3 — E21–E27 and E30. Appended per WS-1 §6 ("WS-2 EXTENDS it").
 *
 * All on site-admin-api's `/v1/admin/data-planes` prefix, all `cp:dp:read` · **site**
 * (`dp-admin.controller.ts`). ⛔ The jobs and lifecycle-write routes are a DIFFERENT service on the
 * `/v1/data-planes` prefix — they live in `provisioningJobs.api.ts` and `dpLifecycle.api.ts`, and
 * ⛔ must not be folded in here. §3: *"A client that 'normalises' them to one base URL breaks half
 * the screen."*
 * ═══════════════════════════════════════════════════════════════════════════
 */

/** E22's row — `DpVersionSummaryEntry` (`dp-admin.service.ts:63-66`). */
export interface DpVersionSummaryEntry {
  readonly version: string;
  readonly count: number;
}

/** An org placed on a data plane — `DpOrgSummary` (`dp-admin.service.ts:69-74`). */
export interface DpOrgSummary {
  readonly id: string;
  readonly slug: string;
  readonly name: string;
  readonly status: string;
}

/**
 * E21's body — `DataPlacementWithDetail` (`dp-admin.service.ts:308-318`).
 *
 * ⭐ It is **flat**: the placement columns sit at the top level with `orgs` / `recentJobs` /
 * `liveStatus` as siblings. The producer's own docblock records why — an April 2026 bug wrapped the
 * placement under a `dataPlane` key, leaving `id` undefined at the top level, and the UI then built
 * `/platform/data-planes/undefined/...` URLs into a 400 cascade. A contract test defends the shape.
 *
 * ⚠️ `liveStatus` is `DpLiveStatus | null` HERE — on the detail route a DP that has never reported
 * is a `null` field rather than the 404 that E11 gives standalone. **Same fact, two shapes**, and
 * both map to `notcollected`; `dp-model.ts` normalises them so the panel cannot treat one as empty.
 */
export interface DataPlaneDetail extends DataPlaneSummary {
  readonly orgs: readonly DpOrgSummary[];
  readonly recentJobs: readonly DataPlaneRecentJob[];
  readonly liveStatus: DpLiveStatus | null;
  /**
   * The AWS placement of an AWS data plane, or `null` (plan 2026-08-25-dp-aws-port, R-056 item 2).
   *
   * The producer is site-admin-api's `DataPlacementSummary.aws` (WS-9g). It reads the newest
   * provisioning job that saved a `spec.aws`. `null` for every provider but `aws`, and `null` for an
   * AWS data plane whose jobs hold no readable `spec.aws`. The Overview tab renders the second case
   * as a stated absence.
   */
  readonly aws: DataPlaneAwsSummary | null;
}

/**
 * The AWS facts of one AWS data plane, as site-admin-api's `DpAwsSummary` puts them on the detail
 * read (WS-9g). Four members, none secret. The saved `subnets` stay on the server.
 */
export interface DataPlaneAwsSummary {
  /** The 12-digit AWS account that holds the data plane. */
  readonly accountId: string;
  /** `aws`, or `aws-us-gov` for a GovCloud region. The CP derives it from the region. */
  readonly partition: 'aws' | 'aws-us-gov';
  /** The zone pair of the data plane, zone A first. */
  readonly availabilityZones: readonly [string, string];
  /** The VPC range of the data plane's allocator slot, a CIDR such as `10.64.0.0/19`. */
  readonly vpcCidr: string;
}

/**
 * A job as the DETAIL route embeds it — `ProvisioningJobSummary` (`dp-admin.service.ts:238-250`).
 *
 * ⚠️ NOT the same shape as `provisioningJobs.api.ts`'s `ProvisioningJob`: this one has no `steps`,
 * and it comes from site-admin-api's own raw-SQL projection rather than provisioning-svc's DTO. Two
 * producers, two types — ⛔ do not unify them; the fields they share are a coincidence of the same
 * underlying table, not a contract.
 */
export interface DataPlaneRecentJob {
  readonly id: string;
  readonly dataPlaneId: string;
  readonly action: string;
  /** Same four-value vocabulary as `provisioningJobs.api.ts` — see that file's `status` docblock. */
  readonly status: string;
  readonly cloudProvider: string;
  readonly region: string;
  readonly error: string | null;
  readonly createdAt: string;
  readonly startedAt: string | null;
  readonly completedAt: string | null;
}

/** E23 — one fixture's per-version rollout status (`dp-admin.service.ts:337-357`). */
export interface DpFixtureStatusBucket {
  readonly appId: string;
  readonly fixtureId: string;
  readonly versions: readonly {
    readonly versionSlug: string;
    readonly version: string;
    readonly health: string;
    readonly readyReplicas: number;
    readonly desiredReplicas: number;
    readonly channels: readonly string[];
    readonly lastRolloutAt: string;
    readonly reportedAt: string;
  }[];
}

/**
 * E24 — `CrossplaneResources` (`provisioning-svc/src/modules/provisioning/crossplane-resources.types.ts:57-62`).
 *
 * ⛔ **THREE INDEPENDENT FACTS, TWO OF THEM NULLABLE — NOT A LIST.** §5.4 said "`[]` ⇒ EmptyState",
 * which can only have meant `managedResources`. The two nullable fields are exactly the ones an
 * operator on a stuck DP most needs distinguished: **no XR exists at all** is a different and far
 * worse fact than *the XR exists with no managed resources yet*. Three separate `empty` cells,
 * ruled (ACK-ws2-5); `notcollected` is reserved for a failed call, because Kubernetes did answer.
 */
export interface CrossplaneResources {
  readonly xr: CrossplaneXr | null;
  readonly managedResources: readonly CrossplaneManagedResource[];
  readonly helmRelease: CrossplaneHelmRelease | null;
  readonly fetchedAt: string;
}

/**
 * ⛔ **THE THREE FIELDS ABOVE WERE `Readonly<Record<string, unknown>>` AND THAT WAS THE SAME DEFECT
 * `DpComputeView` HAD.** This file already records the lesson two hundred lines up: *"a lying read
 * type is exactly how screen 14 would have been built on two properties that never arrive"*. An
 * uninformative type is the softer version of the same problem — it typechecks against **anything**,
 * so a fixture serving `{ a: 1 }` agreed with it (and one in `dp-detail-harness.tsx` did), and every
 * consumer that wants to render a resource has to invent its own narrowing.
 *
 * ⇒ The three shapes below are transcribed from the producer, which is
 * `provisioning-svc/src/modules/provisioning/crossplane-resources.types.ts:19-56` — the file
 * site-admin-api's `getCrossplaneResources` proxies verbatim. ⚠️ They are declared HERE rather than
 * imported because provisioning-svc is a service, not a workspace dependency of this UI package.
 */

/**
 * One Kubernetes `status.conditions[]` entry, surfaced verbatim.
 *
 * ⭐ Managed resources carry **BOTH** `Synced` (Crossplane reconciled the resource) and `Ready` (the
 * provider reports it usable), and the producer states that ⛔ **`Synced=False` is the #1
 * stuck-provision signal** — which is why `reason` and `message` are preserved on every condition
 * rather than reduced to a boolean. A table that renders only `Ready` throws away the field that
 * says *why*.
 */
export interface CrossplaneCondition {
  readonly type: string;
  readonly status: string;
  readonly reason: string;
  readonly message: string;
  readonly lastTransitionTime: string;
}

/** The cluster-scoped `DataPlane` composite resource. `null` ⇒ **no XR exists at all**. */
export interface CrossplaneXr {
  readonly name: string;
  readonly conditions: readonly CrossplaneCondition[];
}

/** A composed managed resource — a cloud MR, a provider-kubernetes `Object`, a helm `Release`. */
export interface CrossplaneManagedResource {
  readonly apiVersion: string;
  readonly kind: string;
  readonly name: string;
  readonly conditions: readonly CrossplaneCondition[];
}

/**
 * The provider-helm `Release` MR's status.
 *
 * ⚠️ `revision` and `lastDeployed` are genuinely nullable and the producer says why: it reads the
 * CP-cluster `Release` MR rather than the DP-cluster Helm storage Secret, precisely because the
 * latter is unreachable during a stuck or early provision. ⛔ So a `null` here is *"best-effort, not
 * decoded"* — not zero, and not "never deployed".
 */
export interface CrossplaneHelmRelease {
  readonly name: string;
  readonly namespace: string;
  readonly revision: number | null;
  readonly state: string;
  readonly chart: string;
  readonly lastDeployed: string | null;
}

/**
 * E25 — one row of the unified Activity timeline (`dp-activity.logic.ts:68-77`).
 *
 * ⛔ **THERE IS NO ACTOR FIELD ON THIS ENDPOINT, AND THAT IS NOT AN OMISSION TO WORK AROUND.**
 * §5.4 cross-referenced WS-1's actor-null rule here; that rule is about `TenantAuditEvent.actorEmail`
 * on `org-admin.service.ts:92-97`, a genuinely different producer. This timeline records no actor
 * at all. Ruled: drop the rule (ACK-ws2-5). ⛔ Do **not** dig one out of `detail` — it is untyped
 * and carries no guarantee of one, so anything found there is a guess rendered as provenance.
 *
 * ⛔ `detail` is never rendered as raw JSON (the same prohibition WS-1 puts on its `details` blob,
 * which DOES transfer, because it is about the shape rather than the field name).
 */
export interface DpActivityEvent {
  readonly id: string;
  readonly ts: string;
  /** ⭐ The most useful field on the row: a job, an audit entry and a version event differ in kind. */
  readonly source: 'job' | 'audit' | 'version';
  readonly severity: 'info' | 'warn' | 'error';
  readonly title: string;
  readonly detail?: Readonly<Record<string, unknown>>;
  readonly refs?: { readonly jobId?: string; readonly logsPodFilter?: string };
}

/** E25's envelope — ⚠️ a PAGE, not a bare array. `events: []` is the empty answer. */
export interface DpActivityPage {
  readonly events: readonly DpActivityEvent[];
  readonly nextCursor: string | null;
}

/**
 * E25's two query parameters.
 *
 * ⛔ **They existed on the producer from day one and this client sent NEITHER**, which is why the
 * timeline was a fixed, unpageable slice with a `nextCursor` nothing read
 * (`dp-admin.controller.ts:142-148`).
 *
 * ⚠️ The producer CLAMPS rather than rejects — `Math.min(Math.max(limit ?? 50, 1), 100)`
 * (`dp-admin.service.ts:733`) — so an out-of-range `limit` silently becomes a different page size
 * than the one asked for. That is exactly why {@link DP_ACTIVITY_PAGE_LIMIT} is a named constant
 * inside the bounds rather than a number chosen at a call site.
 */
export interface DpActivityQuery {
  /** Opaque `(ts, id)` keyset cursor from a prior page's `nextCursor`. */
  readonly cursor?: string;
  /** Page size. Producer default **50**, maximum **100**, clamped server-side. */
  readonly limit?: number;
}

/**
 * The page size this console asks for.
 *
 * ⛔ **Equal to the producer's own default, deliberately.** Page 1 is issued by the pane's
 * `useApiResource` with no query at all, so any other value here would make page 2 a different size
 * from page 1 — a list whose row count changes as you page through it reads as data appearing and
 * disappearing. Sending it EXPLICITLY on later pages (rather than relying on the same default
 * twice) means the page size is a property of the request we can see, not of a server default that
 * can change under us.
 */
export const DP_ACTIVITY_PAGE_LIMIT = 50;

/**
 * E26 — the compute view. ⚠️ Read-only in WS-2; `PATCH` (E81) is WS-6's, below.
 *
 * ⛔ **THE CONTRACT IS IMPORTED, NEVER HAND-DECLARED.** `dpComputeViewSchema`
 * (`cloud/platform-sdk/contracts/src/dp-compute-view.ts:91-97`) is the *single source of truth for
 * the wire shape* by its own declaration, and it is the controller's literal return type
 * (`compute.controller.ts:19`). Importing it means a producer change becomes a typecheck failure
 * here instead of a silent runtime blank — the same rule `billing.api.ts` states for `CostSummary`.
 *
 * ⚠️ **This replaces a hand-rolled `DpCompute` that declared `{ nodeCount?, machineType? }`.**
 * Neither field exists on the producer; the `[key: string]: unknown` index signature is what let
 * the wrong shape typecheck, and a fixture in `DataPlanePane.test.tsx` had already been written to
 * agree with the type rather than with the wire. Nothing rendered either field, so the correction
 * is behaviour-neutral — but a lying read type is exactly how screen 14 would have been built on
 * two properties that never arrive.
 */
export type { DpComputeView, PoolComputeView, PoolAdvisory, DpAdminLimits };

/**
 * E27 — one point of the storage trend. `StorageTrendPoint`
 * (`site-admin-api/src/modules/dp-admin/storage-trend.logic.ts:2-10`).
 *
 * ⛔ **CORRECTED (WS-6). The field names were the METRIC names, not the RESPONSE names.**
 * This interface previously declared `{ at, pvcTotalGi, pvcTotalCount }`. Those three strings do
 * not appear in the response at all: `pvc_total_gi` / `pvc_total_count` are the two **metering
 * metric names the query filters on** (`STORAGE_TREND_METRIC_NAMES`, same file `:12-15`), and
 * `buildStorageTrend` (`:36`) projects them onto `totalSizeGi` / `totalCount` keyed by `timestamp`.
 *
 * ⚠️ Nothing rendered a field off this type before now — WS-2's compute panel is read-only prose and
 * the api-paths test answers `[]` — so the mistake was invisible. Screen 14's disk panel is the
 * first consumer, and against the old names it would have charted a series of `undefined`: a chart
 * that draws, that nobody could call empty, and that means nothing. ⚠️ The dev fixture
 * (`dev/fixtures/platform.json` → `dpStorageTrend`) was written to match the wrong names and is
 * corrected in the same change.
 *
 * ⛔ `totalSizeGi` is an ABSOLUTE PVC total with **no capacity denominator anywhere in the
 * response** ⇒ a utilisation percentage is not computable from this endpoint. Render the trend, and
 * ⛔ never a "% full".
 */
export interface DpStorageTrendPoint {
  /** ISO-8601 period start. */
  readonly timestamp: string;
  /** Total storage in GiB reported for the period. */
  readonly totalSizeGi: number;
  /** Total volume count reported for the period. */
  readonly totalCount: number;
}

// ── Paths. ⛔ Every suffix is a LITERAL; A20 pins each one. ──────────────────

export function dpVersionSummaryPath(): string {
  // ⚠️ NOT `:id/version-summary` — a FLEET-wide histogram with no DP identity in it
  // (`dp-admin.controller.ts:31`). Joining it per row is impossible; §4 explains why it belongs to
  // screen 12's distribution panel rather than to any row.
  return `${DATA_PLANES_PATH}/version-summary`;
}

export function dpFixtureStatusPath(dpId: string): string {
  return `${dataPlanePath(dpId)}/fixture-status`;
}

export function dpCrossplaneResourcesPath(dpId: string): string {
  return `${dataPlanePath(dpId)}/crossplane-resources`;
}

/**
 * ⛔ **The empty query MUST produce the bare path, byte-for-byte.** `DataPlanePane` uses this string
 * as its `useApiResource` cache key and A20 pins the literal, so a trailing `?` would be a different
 * string in both places — the same rule (and the same one-line guard) as {@link tracesPath}.
 */
export function dpActivityPath(dpId: string, query: DpActivityQuery = {}): string {
  const params = new URLSearchParams();
  // ⛔ Absent filters are OMITTED, never emitted as the string `undefined`.
  if (query.cursor !== undefined) params.set('cursor', query.cursor);
  if (query.limit !== undefined) params.set('limit', String(query.limit));
  const qs = params.toString();
  return qs === '' ? `${dataPlanePath(dpId)}/activity` : `${dataPlanePath(dpId)}/activity?${qs}`;
}

export function dpComputePath(dpId: string): string {
  return `${dataPlanePath(dpId)}/compute`;
}

export function dpStorageTrendPath(dpId: string): string {
  return `${dataPlanePath(dpId)}/storage-trend`;
}

/** E30 — the three Kubernetes reads, `dp-admin.controller.ts:111`, `:121`, `:131`. */
/**
 * One pod on a data plane, as `GET …/:id/k8s/pods` sends it.
 *
 * ## ⛔ TRACED THROUGH THREE HOPS, BECAUSE THE NEAREST TYPE WOULD HAVE MISLED ME
 *
 * site-admin-api proxies to provisioning-svc, which proxies to the DP. Two traps on the way, both
 * measured rather than assumed:
 *
 *  1. ⛔ **The DP's own contract says `items`; this route says `pods`.**
 *     `ListPodsResponse` in `@zion/sdk-cp-dp-contracts` is `{ items: PodSummary[] }`, but
 *     `KubernetesService.listPods` re-shapes it — `const pods = res.items.map(mapRestPodToLegacy)`
 *     (`kubernetes.service.ts:31-39`) — so what reaches a browser is `{pods, namespace, fetchedAt}`.
 *     Declaring `items` off the published contract would have typechecked and read `undefined`.
 *  2. ⛔ **site-admin-api's own `CorePodInfo` OMITS `name`** (`version-reconcile.logic.ts:19-22`,
 *     `{attribution?, containers?}`) because the version gate does not read it. It is a PARTIAL
 *     view, not the wire — and `name` is exactly the field this filter needs. Trusting the nearest
 *     interface would have produced a control with no values in it.
 *
 * ⇒ the authority is `KubePodInfo` (`provisioning-svc/src/lib/kube-client.ts:64-76`), which is what
 * `mapRestPodToLegacy` produces. `name` is REQUIRED there; `nodeName` and `ageSeconds` are genuinely
 * optional (absent while unscheduled, or on deploy skew).
 *
 * ⚠️ Declared as a PARTIAL on purpose: the wire carries `status`, `ready`, `restarts`, `containers`
 * and more, and this module reads only what the pod filter needs. ⛔ That is safe in a way the
 * `CorePodInfo` trap above was not — omitting a field you do not read is fine; declaring one the
 * producer never sends, or mistyping its nullability, is what breaks silently.
 */
export interface DpPodName {
  /** ⛔ REQUIRED on the wire. The value the log query's `pods` filter matches on. */
  readonly name: string;
  /** The scheduled node, absent while the pod is unscheduled. */
  readonly nodeName?: string;
}

export interface DpPodsResponse {
  readonly pods: readonly DpPodName[];
  readonly namespace: string;
  /** ISO-8601 — when the CP asked, not when the pod changed. */
  readonly fetchedAt: string;
}

/**
 * The DP's live pod list — the pod filter's only honest source of options.
 *
 * ⛔ Gated `cp:dp:read` (`kubernetes.controller.ts:32`), the same permission as the DP list, so it
 * does not widen the screen's permission surface.
 *
 * ⚠️ It AUDITS: `getK8sPods` writes a `dp.k8s_query` audit row (`dp-admin.service.ts:1027-1031`).
 * That is fine for a filter the operator opened deliberately; ⛔ do not put this on a poll.
 */
export function getDpK8sPods(dpId: string): Promise<DpPodsResponse> {
  return api.get<DpPodsResponse>(dpK8sPodsPath(dpId));
}

export function dpK8sPodsPath(dpId: string): string {
  return `${dataPlanePath(dpId)}/k8s/pods`;
}

export function dpK8sEventsPath(dpId: string): string {
  return `${dataPlanePath(dpId)}/k8s/events`;
}

/**
 * One Kubernetes event on a data plane, as `GET …/:id/k8s/events` sends it.
 *
 * ## ⛔ THE PATH HELPER SHIPPED WITHOUT A FETCHER, AND THE DECK'S FOURTH INSPECT PANEL IS WHY
 *
 * `mockups/13-data-plane.html` specifies **Logs · Metrics · Traces · Kubernetes (pods · events)** and
 * only three were built. The route was live the whole time — `cp:dp:read`, the same grant the detail
 * read needs — so the panel was unbuilt rather than unbuildable.
 *
 * ## ⛔ THIS IS THE **LEGACY** SHAPE, NOT THE WIRE CONTRACT, AND THE DIFFERENCE IS DELIBERATE
 *
 * There are two events routes on provisioning-svc and they are documented as two audiences:
 * `:id/k8s/events` (this one) re-maps through `mapRestEventToLegacy`, while `:id/k8s/app-events`
 * passes `ListEventsResponse` through verbatim for the app-scoped surface. ⛔ Do not "upgrade" this
 * to the raw contract — `kubernetes.service.ts:60-67` states that widening the platform-admin route
 * to serve an app audience is the conflation that split exists to end.
 *
 * ⚠️ Two consequences of the legacy mapping that this UI has to render honestly:
 *
 *  1. **`involvedObject` is a FLATTENED `"Kind/name"` string**, not the object. `kind` is lost as a
 *     separate field, so nothing here can filter by kind without re-parsing a display string — and
 *     it deliberately does not try.
 *  2. ⛔ **`firstTimestamp` / `lastTimestamp` collapse ABSENT to the EMPTY STRING**
 *     (`kube-client`'s mapper: `item.firstTimestamp ?? ''`). The app-facing contract types the same
 *     field `string | null` precisely because empty and absent are different facts. ⇒ `''` here means
 *     **not recorded**, and it must never be formatted as a date or rendered as a blank cell — a
 *     blank reads as "nothing happened then", which is the empty-vs-not-collected collapse this
 *     package exists to refuse.
 */
export interface DpK8sEvent {
  /** ⚠️ Already normalised by the producer: anything not `Warning` arrives as `Normal`. */
  readonly type: 'Normal' | 'Warning';
  readonly reason: string;
  readonly message: string;
  /** Flattened `"Kind/name"`. See trap 1 above. */
  readonly involvedObject: string;
  /** Seconds since `lastTimestamp`, computed server-side at request time. */
  readonly ageSeconds: number;
  /** How many times this event has fired. `1` is an event; `47` is a symptom. */
  readonly count: number;
  /** ⛔ `''` means NOT RECORDED. See trap 2 above. */
  readonly firstTimestamp: string;
  /** ⛔ `''` means NOT RECORDED. See trap 2 above. */
  readonly lastTimestamp: string;
}

export interface DpK8sEventsResponse {
  readonly events: readonly DpK8sEvent[];
  readonly namespace: string;
  /** ISO-8601 — when the CP asked, not when the cluster changed. */
  readonly fetchedAt: string;
}

/**
 * The DP's namespace events.
 *
 * ⚠️ It AUDITS, exactly like {@link getDpK8sPods}: `getK8sEvents` writes a `dp.k8s_query` row
 * (`dp-admin.service.ts:1058`). ⛔ Do not put this on a poll, and do not call it from two components
 * — one read per panel, or an operator opening a tab writes two audit rows for one question.
 */
export function getDpK8sEvents(dpId: string): Promise<DpK8sEventsResponse> {
  return api.get<DpK8sEventsResponse>(dpK8sEventsPath(dpId));
}

export function dpK8sResourcesPath(dpId: string): string {
  return `${dataPlanePath(dpId)}/k8s/resources`;
}

// ── Readers ─────────────────────────────────────────────────────────────────

/** E21 — the flat detail view, with orgs, recent jobs and the embedded live status. */
export function getDataPlaneDetail(dpId: string): Promise<DataPlaneDetail> {
  return api.get<DataPlaneDetail>(dataPlanePath(dpId));
}

/** E22 — the fleet version histogram. ⛔ No DP identity; never joined per row. */
export function getDpVersionSummary(): Promise<readonly DpVersionSummaryEntry[]> {
  return api.get<readonly DpVersionSummaryEntry[]>(dpVersionSummaryPath());
}

/** E23 — per-app, per-fixture rollout status on this DP. */
export function getDpFixtureStatus(dpId: string): Promise<readonly DpFixtureStatusBucket[]> {
  return api.get<readonly DpFixtureStatusBucket[]>(dpFixtureStatusPath(dpId));
}

/** E24 — the Crossplane resource graph. See {@link CrossplaneResources} for the three cells. */
export function getDpCrossplaneResources(dpId: string): Promise<CrossplaneResources> {
  return api.get<CrossplaneResources>(dpCrossplaneResourcesPath(dpId));
}

/** E25 — the activity timeline page. */
export function getDpActivity(dpId: string, query: DpActivityQuery = {}): Promise<DpActivityPage> {
  return api.get<DpActivityPage>(dpActivityPath(dpId, query));
}

/** E26 — compute. ⚠️ Screen 13 is READ-ONLY: A19 asserts it issues no `PATCH` to this path. */
export function getDpCompute(dpId: string): Promise<DpComputeView> {
  return api.get<DpComputeView>(dpComputePath(dpId));
}

/** E27 — the storage trend series. */
export function getDpStorageTrend(dpId: string): Promise<readonly DpStorageTrendPoint[]> {
  return api.get<readonly DpStorageTrendPoint[]>(dpStorageTrendPath(dpId));
}

/*
 * ═══════════════════════════════════════════════════════════════════════════
 * R7 WS-6 §3 — E81–E85, screen 14 (Fleet capacity). Appended per WS-1 §6 / WS-2's own precedent.
 *
 * ⚠️ **E86 (`…/:id/metrics/query`) IS NOT HERE AND MUST NOT BE ADDED.** WS-6 §6's file list says to
 * add it; WS-2 already shipped it as `metricsQueryPath` / `queryMetrics` in `dpObservability.api.ts`
 * (`:141-153`), with all four required params and its literal query string already pinned by
 * `dp-api-paths.test.ts:157-158`. A second declaration of the same endpoint is the drift this file's
 * two-service header exists to prevent, pointed inward.
 *
 * ⛔ **NEITHER OF THE TWO CATALOG READS IS FLEET-WIDE, AND THAT SHAPES THE SCREEN.**
 * There is no fleet compute roll-up (WS-6 §1.1) and no fleet machine-type catalog (E82 is scoped to
 * one `(region, provider)` pair, below), so screen 14 is a **client-side fan-out on two different
 * keys** — per data plane for E26/E27/E83/E85, and per distinct `(region, provider)` for E82.
 * ═══════════════════════════════════════════════════════════════════════════
 */

/**
 * E83 / E84's payload — `DataPlaneMachineTypesResponse`
 * (`provisioning-svc/src/modules/provisioning/dto/machine-type-catalog.dto.ts:30-40`, proxied
 * verbatim by site-admin-api).
 *
 * ⚠️ Hand-declared: the ENVELOPE is a provisioning-svc DTO and `@zion/provisioning-svc` is not (and
 * must not become) a dependency of a browser bundle. The ENTRY types inside it are real contract
 * exports, so they are imported — the drift surface is four field names, not four schemas.
 *
 * ## ⛔ `bootDiskType` — SERVER-COMPUTED, OPTIONAL IN THE SCHEMA, AND THE UI MUST NEVER DERIVE IT
 *
 * ⭐ There is **no DTO edit here for it, and that is the point**: `MachineTypeCatalogEntry` is
 * imported from `@zion/sdk-contracts` (see this file's import block), so the field arrives on every
 * entry for free. Do not re-declare it locally.
 *
 * ⭐⭐ **D13 — IT IS ON ALL THREE LISTS, NOT JUST THE BROWSE ONE.** provisioning-svc attaches it
 * server-side at read time on **E82's `machineTypes`** AND on **BOTH of E83's lists** —
 * {@link DataPlaneMachineTypes.enabledMachineTypes} and
 * {@link DataPlaneMachineTypes.provisionable}. ⇒ the ENABLED / in-use views can state the boot disk
 * too; a consumer must not assume the field is a property of the provisionable catalogue alone.
 * ⚠️ This is what makes {@link patchDpMachineTypes}'s confirmation able to name the disk of the type
 * it is keeping — that clause reads `enabledMachineTypes[0]`, so it depends on D13 specifically.
 *
 * ⛔ **`bootDiskTypeForMachineType` is the ONE source of truth and it lives CP-side** (hyperdisk
 * boot-disk D1). The value is **compute-on-read and never persisted** (D5): provisioning-svc
 * derives it from the `nodeType` on the response path. ⇒ ⛔ **this console must never infer a disk
 * type from a machine-type name** — no prefix split, no family list, no `startsWith('c4')`. A second
 * copy of that map in a browser bundle is the dual-source defect D1 exists to prevent, and it would
 * drift silently the first time GCE ships a family.
 *
 * ⚠️ **D13 DOES NOT MAKE IT NON-OPTIONAL, AND THE ABSENCE ARM STAYS ON EVERY PATH.** D13 is a
 * statement about what THIS server attaches; it is not a guarantee about what arrives. The schema
 * field is `.optional()` (D5) for a legacy persisted row or an older CP, and this console talks to
 * whatever CP is deployed. ⛔ A field that is *"always populated in practice"* is exactly the one
 * whose absence arm gets deleted as dead code and then renders a blank in production.
 * ⇒ Render an absence marker (`CellAbsence`, `label="not stated"`), never a blank and above all
 * never a defaulted `pd-ssd` — *"nobody told us"* and *"it boots from PD SSD"* are different facts,
 * and one of them is the wedge this whole change exists to close.
 *
 * ⚠️ **E84 echoes the entry back VERBATIM** — see {@link patchDpMachineTypes}. Both PATCH DTOs
 * validate the body with `z.array(machineTypeCatalogEntrySchema)`, the same schema the field was
 * added to, so the round-trip is shape-valid. Whether the producer PERSISTS what it receives is
 * provisioning-svc's problem (D5 says it must not), not this console's: ⛔ do not "helpfully" strip
 * the key on the way out — a client that edits a body it does not own is a client that will
 * eventually strip the wrong one.
 *
 * ⭐ `storageClasses` is the ENABLED subset and `provisionableStorageClasses` the full catalog; the
 * two are not interchangeable, and the producer's own comment (`:35-36`) says so.
 */
export interface DataPlaneMachineTypes {
  readonly enabledMachineTypes: readonly MachineTypeCatalogEntry[];
  readonly storageClasses: readonly StorageClassCatalogEntry[];
  readonly provisionable: readonly MachineTypeCatalogEntry[];
  readonly provisionableStorageClasses: readonly StorageClassCatalogEntry[];
  /**
   * The live fixture-service projection. It lets the screen state the true reason for a disabled
   * lever: the producer answers 409 `MACHINE_TYPE_IN_USE` when live fixture services still use a
   * type that the PATCH removes, so the screen does not show the 409 as a generic failure.
   */
  readonly inUse: readonly { readonly nodeType: string; readonly liveCount: number }[];
}

/**
 * E82's payload — `MachineTypeCatalogResponse` (same DTO file, `:21-28`).
 *
 * ⚠️ `materializedAt: null` means the catalog has never been discovered for this
 * `(region, provider)` — a **producer** statement, so it renders `notcollected` rather than as an
 * empty catalog. ⛔ An empty `machineTypes` with a non-null `materializedAt` is the genuinely
 * empty answer; the two are different facts and collapse into the same blank if you let them.
 *
 * ⛔⛔ **AND A `materializedAt: null` RESPONSE CAN STILL CARRY A POPULATED `machineTypes`** — the dev
 * fixture `gcp|us-east1` is exactly that shape, and it is realistic. ⇒ a consumer that lists the
 * entries **must skip this pair entirely**, or the screen simultaneously says *"never discovered"*
 * and lists eight types it claims were never discovered. `buildCatalogEntryRows`
 * (`panes/capacity-model.ts`) is where that skip lives.
 *
 * ⚠️ Every entry carries `bootDiskType` — see {@link DataPlaneMachineTypes} for the rule (imported,
 * server-computed, optional, never derived here).
 */
export interface MachineTypeCatalog {
  readonly region: string;
  readonly provider: string;
  readonly machineTypes: readonly MachineTypeCatalogEntry[];
  readonly storageClasses: readonly StorageClassCatalogEntry[];
  readonly materializedAt: string | null;
}

/** The four providers `menuQuerySchema` accepts (`machine-types.dto.ts:7`). */
export const MACHINE_TYPE_PROVIDERS = ['local', 'gcp', 'aws', 'azure'] as const;
export type MachineTypeProvider = (typeof MACHINE_TYPE_PROVIDERS)[number];

/** E81 / E84's gate. ⛔ A DIFFERENT permission from the reads, and separately holdable. */
export const DP_UPDATE_PERMISSION = 'cp:dp:update';

// ── Paths ───────────────────────────────────────────────────────────────────

/**
 * E82 — the region-scoped provisionable catalog.
 *
 * # ⛔⛔ `region` AND `provider` ARE REQUIRED. A BARE REQUEST IS A 400.
 *
 * `menuQuerySchema` (`site-admin-api/src/modules/machine-types/dto/machine-types.dto.ts:5-12`)
 * declares `region: z.string().min(1)` and `provider: z.enum([…])` with **no `.optional()` on
 * either**, and `ZionStrictValidationPipe` is wired globally, so the omission is rejected at the
 * boundary. ⇒ there is **no fleet-wide catalog call**: the screen issues one request per distinct
 * `(region, provider)` pair present in E10's rows.
 *
 * ⛔ **An MSW test cannot catch this** — a fake answers whatever its handler says regardless of the
 * query string, and a path assertion written against a bare `/v1/admin/machine-types` would *pin*
 * the 400. Both are required positionals here so the broken call is unrepresentable, and
 * `dp-api-paths.test.ts` asserts the literal string. Same trap `billing.api.ts` records for
 * `startDate`/`endDate`, on a different endpoint.
 *
 * ⛔ `refresh` is deliberately NOT exposed. It is `z.enum(['true','false'])` and triggers a live
 * cloud-provider discovery on the producer; a read-only capacity screen must not be able to.
 */
export function machineTypesMenuPath(region: string, provider: MachineTypeProvider): string {
  const params = new URLSearchParams({ region, provider });
  return `/v1/admin/machine-types?${params.toString()}`;
}

/** E83 / E84 — this data plane's enabled + provisionable machine types. */
export function dpMachineTypesPath(dpId: string): string {
  return `${dataPlanePath(dpId)}/machine-types`;
}

/**
 * 2026-09-04-app-pools WS-3, console (R-013). The path of the operator's app-pool read, and the
 * cache key `useApiResource` reads.
 *
 * It substitutes into `APP_POOL_ADMIN_PATH` rather than re-spelling the route. The contract owns
 * the whole path; the controller's decorator owns only its last two segments (its
 * `@Controller('v1/admin/data-planes')` supplies the prefix). Building the string here from
 * `dataPlanePath` would be a third spelling, and the one nothing checks against the other two.
 *
 * And it is the cache key, so it must be stable per data plane and must not carry a nonce. Same
 * shape as {@link dpMachineTypesPath} directly above.
 */
export function dpAppPoolsPath(dpId: string): string {
  return APP_POOL_ADMIN_PATH.replace(':dpId', encodeURIComponent(dpId));
}

/**
 * E85 — the DP's scaling-event timeline.
 *
 * ⚠️ `scalingEventQuerySchema` (`contracts/src/scaling-event.ts:217-226`) is `.strict()` and every
 * member is `.optional()`, so a bare call is valid — but an **unknown key is a 400**, which is why
 * this takes the contract's own query type rather than a loose bag. ⛔ And an omitted filter is
 * omitted, never emitted as the string `undefined`: `class=undefined` fails the enum and 400s.
 */
export function dpScalingEventsPath(dpId: string, query: ScalingEventQuery = {}): string {
  const params = new URLSearchParams();
  if (query.since !== undefined) params.set('since', String(query.since));
  if (query.limit !== undefined) params.set('limit', String(query.limit));
  if (query.class !== undefined) params.set('class', query.class);
  const qs = params.toString();
  // ⛔ No trailing bare `?` when there are no filters — a different string, and a real 400 risk.
  return qs === '' ? `${dataPlanePath(dpId)}/scaling-events` : `${dataPlanePath(dpId)}/scaling-events?${qs}`;
}

// ── Readers ─────────────────────────────────────────────────────────────────

/** E82 — the catalog for ONE `(region, provider)`. See {@link machineTypesMenuPath}. */
export function getMachineTypeCatalog(
  region: string,
  provider: MachineTypeProvider,
): Promise<MachineTypeCatalog> {
  return api.get<MachineTypeCatalog>(machineTypesMenuPath(region, provider));
}

/** E83 — this DP's current machine-type selection, against the provisionable set. */
export function getDpMachineTypes(dpId: string, signal?: AbortSignal): Promise<DataPlaneMachineTypes> {
  return api.get<DataPlaneMachineTypes>(dpMachineTypesPath(dpId), { signal });
}

/**
 * 2026-09-04-app-pools WS-3, console (R-013). The app pools this data plane reports, joined with
 * the CP's refusal record.
 *
 * It parses the body with `appPoolAdminResponseSchema`, which is the shape WS-1 declares, and it is
 * not a bare `api.get<T>`. The response's `rows` is `.nullable()`, and the panel renders three
 * different things for `null`, `[]` and a populated array. So a body that silently arrived with
 * `rows` absent would render as never-reported when in fact the CP answered something else. Parsing
 * turns that into an error the panel shows.
 *
 * Errors the caller renders: the route answers `502` with the code `dp-report-invalid` when the CP
 * re-parse of a reported row fails (R-037). One malformed row fails the whole read, so the panel
 * has no partial table to show and renders `ErrorState` for this data plane. The `502` body names
 * the row index and the failing key and carries no operator-supplied value, so the panel may show
 * the server's message as it stands.
 *
 * Follows `getDpMachineTypes` directly above, with the one addition of the parse.
 */
export async function getDpAppPools(dpId: string, signal?: AbortSignal): Promise<AppPoolAdminResponse> {
  // The body is parsed here. `rows` is `.nullable()` and the panel renders three
  // different things for `null`, `[]` and a populated array. A body that arrived with `rows`
  // absent would show as never-reported when the CP answered something else. `signal` aborts the
  // request: the tier cap Set reads the report again under its own time bound before it writes.
  return appPoolAdminResponseSchema.parse(await api.get<unknown>(dpAppPoolsPath(dpId), { signal }));
}

/** E85 — the scaling-event page. ⚠️ `ScalingEventPage.nextSince` is OPTIONAL on the contract. */
export function getDpScalingEvents(
  dpId: string,
  query: ScalingEventQuery = {},
): Promise<ScalingEventPage> {
  return api.get<ScalingEventPage>(dpScalingEventsPath(dpId, query));
}

// ── Writers. ⛔ Both are `cp:dp:update` · site ⇒ HIDDEN together without it (A14). ───────────

/**
 * E81 — edit a DP's compute. `PATCH`, and the producer answers `200` with an empty body.
 *
 * ⛔ `limits` has **REPLACE semantics**: an omitted sub-field CLEARS that bound
 * (`dp-compute-view.ts:135-141`). Omitting `limits` ENTIRELY leaves the stored row untouched, which
 * is a third, different outcome from sending `{}`. The confirmation copy must therefore name the
 * new value rather than say "update limits" — A14.
 */
/*
 * ⛔⛔ `PerZoneDpComputePatch`, NOT `DpComputePatch` — C-178. THIS ANNOTATION IS THE CONSOLE'S RENAME
 * GATE.
 *
 * The WIRE schema accepts both the per-zone spelling and the deprecated pre-rename one for one release
 * cycle (C-072), because an already-loaded console bundle keeps sending the old keys after the API
 * rolls — a browser cannot be redeployed. ⇒ Under that union `{ pools: { infra: { minNodes, maxNodes } } }`
 * STILL TYPECHECKS, so during the rename the compiler could not say which console sites were missed
 * and a hand-classified grep was the only instrument. It missed a file twice (C-175, C-179).
 *
 * ⇒ **Liberal in what the server accepts, strict in what our own code may construct.** Every console
 * caller of this function goes through this parameter, so narrowing it here makes an un-renamed site a
 * COMPILE ERROR again — measured: reverting one call site to the old spelling reds `typecheck`.
 *
 * ⚠️ Nothing about the request changes. `PerZoneDpComputePatch` is assignable to `DpComputePatch`, so
 * the body crosses to the wire unchanged; only what this codebase may BUILD is narrowed.
 */
export function patchDpCompute(dpId: string, body: PerZoneDpComputePatch, signal?: AbortSignal): Promise<void> {
  return api.patch<void>(dpComputePath(dpId), body, { signal });
}

/**
 * E84 — persist the enabled machine-type subset.
 *
 * ⛔ `enabledMachineTypes` is `.min(1)` — *"D15.10 never zero: a PATCH may not clear the enabled
 * set"* (`machine-types.dto.ts:21-23`) — so an empty array is a 422, not a clear. ⚠️ And the
 * producer refuses with **409** when live fixture services still use a type that the PATCH
 * disables; that is a STATE gate, so the control renders disabled with the true reason drawn from
 * {@link DataPlaneMachineTypes.inUse}, never hidden.
 */
export function patchDpMachineTypes(
  dpId: string,
  body: {
    readonly enabledMachineTypes: readonly MachineTypeCatalogEntry[];
    readonly storageClasses?: readonly StorageClassCatalogEntry[];
  },
  signal?: AbortSignal,
): Promise<void> {
  return api.patch<void>(dpMachineTypesPath(dpId), body, { signal });
}

/**
 * §1.1 — the per-DP compute fan-out, **bounded and reported**.
 *
 * ⛔ `Promise.allSettled`, never `Promise.all`: one 500 must not blank the fleet. ⛔ And never a
 * SILENT partial — the failed ids come back in {@link FanOut.failedIds} so the screen can render
 * *"k of n data planes could not be read"* (**A1**). A fleet figure summed over the survivors is
 * worse than no figure, because it silently excludes the data plane that is on fire.
 */
export async function fetchDpComputes(
  dpIds: readonly string[],
): Promise<FanOut<DpComputeView>> {
  const settled = await Promise.allSettled(dpIds.map((id) => getDpCompute(id)));
  return collectFanOut(dpIds, settled);
}

/** §1.1 — the per-DP storage-trend fan-out. Same discipline as {@link fetchDpComputes}. */
export async function fetchDpStorageTrends(
  dpIds: readonly string[],
): Promise<FanOut<readonly DpStorageTrendPoint[]>> {
  const settled = await Promise.allSettled(dpIds.map((id) => getDpStorageTrend(id)));
  return collectFanOut(dpIds, settled);
}

/** §4 — the per-DP machine-type fan-out. A failure ⇒ that cell `not-collected`, not the screen. */
export async function fetchDpMachineTypes(
  dpIds: readonly string[],
): Promise<FanOut<DataPlaneMachineTypes>> {
  const settled = await Promise.allSettled(dpIds.map((id) => getDpMachineTypes(id)));
  return collectFanOut(dpIds, settled);
}

/** §4 — the per-DP scaling-event fan-out. */
export async function fetchDpScalingEvents(
  dpIds: readonly string[],
): Promise<FanOut<ScalingEventPage>> {
  const settled = await Promise.allSettled(dpIds.map((id) => getDpScalingEvents(id)));
  return collectFanOut(dpIds, settled);
}
